import { Bell, Check, Hash, Lightning } from "@phosphor-icons/react";
import { Badge, Box, Button, Flex, Heading, Text } from "@radix-ui/themes";
import { useState } from "react";

/**
 * In-app Slack viewer (S6 step 4 of the build brief, also reused by C5
 * of the demo plan).
 *
 * Renders the 10 seeded #vitalora-reviews messages plus the Priya DM
 * with the [Yes / No / Remind me at 3pm] buttons. The buttons drive
 * client-side state only — clicking "Yes" hides the buttons and shows
 * the agent's confirmation reply, matching the build brief's
 * scripted dialogue. The seeded `composite_task_id:
 * demo-vitalora-priya-reminder` IntegrationEvent in the seed already
 * carries this exact text so a backend round-trip stays consistent.
 */
type ReplyState = null | "yes" | "no" | "remind";

const SLACK_MESSAGES: {
  author: string;
  initials: string;
  ts: string;
  body: string;
  meta?: string;
  type?: "system" | "human" | "agent";
}[] = [
  {
    author: "Puntt AI",
    initials: "PA",
    ts: "8:50 AM",
    body: "Morning sweep complete. 14 assets in queue, 3 awaiting compliance, 1 escalation overdue — summary in-thread.",
    meta: "auto",
    type: "agent",
  },
  {
    author: "Puntt AI",
    initials: "PA",
    ts: "8:52 AM",
    body: 'VitaCore 400g (#4421) — claim "29.6g protein per serving" flagged. Brand Bible §3.2 logo safe zone confirmed clear. Reg Playbook §2 precedent (2026-02-14, T. Walsh): rounding 29.6g → 30g permitted if labelling sheet amended to ≥29.5g. Awaiting Priya sign-off.',
    type: "agent",
  },
  {
    author: "James Rivera",
    initials: "JR",
    ts: "9:01 AM",
    body: "Was the labelling sheet ever amended? Studio is using 30g on the artwork.",
    type: "human",
  },
  {
    author: "Tom Walsh",
    initials: "TW",
    ts: "9:04 AM",
    body: "Sheet is at 29.6g — needs the formal amendment per the Feb precedent. I can sign it off today if Priya wants to push the artwork as-is, otherwise revert to 27g per the older spec.",
    type: "human",
  },
  {
    author: "Sarah Chen",
    initials: "SC",
    ts: "9:08 AM",
    body: "Tom — please push the amendment, we're not redoing the artwork at this stage. Thanks both.",
    type: "human",
  },
  {
    author: "Puntt AI",
    initials: "PA",
    ts: "9:10 AM",
    body: "VitaCore 800g (#4422) — all 5 checks passed (Brand · Regulatory · Copy · Legal · Dimensions). Auto-approved per routing rule R-12. Stakeholders notified.",
    type: "agent",
  },
  {
    author: "Mia Okonkwo",
    initials: "MO",
    ts: "9:21 AM",
    body: "Reuploaded Luminos SPF30 final — Vitamin B12 added back to the ingredient panel. Should clear the §3.2 ingredient-list rule now.",
    type: "human",
  },
  {
    author: "Puntt AI",
    initials: "PA",
    ts: "9:23 AM",
    body: "Re-checked Luminos SPF30 — Vitamin B12 confirmed. Visual ID Guide §2 ingredient-panel rule now passes. Routing to James (Brand) for MEDIUM-RISK review.",
    type: "agent",
  },
  {
    author: "Puntt AI",
    initials: "PA",
    ts: "11:02 AM",
    body: "Luminos Social Brief (#4398) still incomplete after 4 days — missing target audience and asset dimensions, 2-day window for 11 assets is below studio minimum (auto-adjusted to 5). James Rivera tagged.",
    type: "agent",
  },
  {
    author: "James Rivera",
    initials: "JR",
    ts: "11:18 AM",
    body: "On it — pulling audience details from Sarah's deck and posting dimensions per platform now.",
    type: "human",
  },
  {
    author: "Puntt AI",
    initials: "PA",
    ts: "1:10 PM",
    body: "Tom Walsh load check — 11 overdue, 8.4-day avg response. Suggested reassigning protein claim review to Priya Patel (2.1-day avg, 1 active task). DM sent for confirmation.",
    type: "agent",
  },
];

export default function SlackViewer() {
  const [reply, setReply] = useState<ReplyState>(null);

  return (
    <Box>
      <Flex align="center" gap="2" mb="3">
        <Hash size={20} weight="bold" className="text-puntt-accent-9" />
        <Heading size="4">vitalora-reviews</Heading>
        <Badge color="grass" variant="soft">
          <Check size={12} /> Bot active
        </Badge>
      </Flex>

      <div className="max-h-96 overflow-y-auto rounded-lg border border-puntt-neutral-gray-6 bg-base-white p-3">
        <Flex direction="column" gap="3">
          {SLACK_MESSAGES.map((m) => (
            <Flex key={`${m.author}-${m.ts}`} gap="3">
              <div
                className={`flex size-8 shrink-0 items-center justify-center rounded-md text-xs font-semibold ${
                  m.type === "agent"
                    ? "bg-puntt-accent-3 text-puntt-accent-11"
                    : "bg-puntt-neutral-gray-4 text-puntt-neutral-gray-12"
                }`}
              >
                {m.initials}
              </div>
              <Box className="flex-1">
                <Flex align="baseline" gap="2">
                  <Text size="2" weight="bold">
                    {m.author}
                  </Text>
                  {m.type === "agent" ? (
                    <Badge color="iris" variant="soft">
                      app
                    </Badge>
                  ) : null}
                  <Text size="1" color="gray">
                    {m.ts}
                  </Text>
                </Flex>
                <Text size="2">{m.body}</Text>
              </Box>
            </Flex>
          ))}

          {/* Priya DM, with action buttons */}
          <div className="border-puntt-warn-7 bg-puntt-warn-2 rounded-lg border p-3">
            <Flex align="center" gap="2" mb="2">
              <Bell size={14} className="text-puntt-warn-11" />
              <Text size="1" color="gray">
                DM from Puntt AI to @Priya Patel · 9:01 AM
              </Text>
            </Flex>
            <Text size="2">
              VitaCore Final Artwork (#4421) has been with you for 6 days —
              launch is at risk today. Reg Playbook §2 precedent (2026-02-14)
              applies; only the labelling-sheet amendment is blocking sign-off.
              Full review summary attached. If no response by 17:00, should I
              reassign to Alex Drummond and notify Sarah?
            </Text>
            {reply === null ? (
              <Flex gap="2" mt="2">
                <Button
                  data-testid="slack-viewer-reply-yes"
                  onClick={() => setReply("yes")}
                >
                  Yes
                </Button>
                <Button
                  data-testid="slack-viewer-reply-no"
                  variant="soft"
                  color="gray"
                  onClick={() => setReply("no")}
                >
                  No
                </Button>
                <Button
                  data-testid="slack-viewer-reply-remind"
                  variant="soft"
                  color="gray"
                  onClick={() => setReply("remind")}
                >
                  Remind me at 3pm
                </Button>
              </Flex>
            ) : (
              <Flex gap="2" mt="2" align="center">
                <Lightning size={14} className="text-puntt-warn-11" />
                <Text size="2">
                  {reply === "yes"
                    ? "Understood. If no response by 17:00, reassign to Alex Drummond and notify both parties. Logged in audit trail."
                    : reply === "no"
                      ? "Got it. I will monitor and re-prompt only if the launch slips past today."
                      : "OK — I will re-ping at 15:00 and update the activity feed."}
                </Text>
              </Flex>
            )}
          </div>
        </Flex>
      </div>
    </Box>
  );
}
